import React, { useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Navigation } from './Navigation';
import { PullToRefresh } from '@/components/common';
import { useAuth } from '@/components/auth';
import { useAppStore } from '@/store';

/**
 * Skip Link Component for keyboard navigation (WCAG 2.1 AA - 2.4.1 Bypass Blocks)
 */
function SkipLink(): React.ReactElement {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-9999 focus:px-4 focus:py-2 focus:bg-cherry-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-cherry-600"
    >
      Zum Hauptinhalt springen
    </a>
  );
}

export function Layout(): React.ReactElement {
  const location = useLocation();
  const { getAccessToken } = useAuth();
  const fetchDataFromBackend = useAppStore((state) => state.fetchDataFromBackend);

  const isRatingFlow =
    location.pathname.startsWith('/rating') || location.pathname.startsWith('/evaluate');

  // Pull-to-refresh handler - lädt Daten neu aus dem Backend
  const handleRefresh = useCallback(async (): Promise<void> => {
    const token = await getAccessToken();
    if (token) {
      await fetchDataFromBackend(token);
    }
  }, [getAccessToken, fetchDataFromBackend]);

  // Pull-to-refresh nur auf Hauptseiten, nicht im Rating-Flow
  const enablePullToRefresh = !isRatingFlow;

  return (
    <div className="h-dvh bg-charcoal-50 flex flex-col overflow-hidden">
      <SkipLink />
      <Header />
      <main
        id="main-content"
        className="flex-1 overflow-y-auto min-h-0"
        tabIndex={-1}
        aria-label="Hauptinhalt"
      >
        <PullToRefresh onRefresh={handleRefresh} enabled={enablePullToRefresh}>
          <Outlet />
        </PullToRefresh>
      </main>
      <Navigation />
    </div>
  );
}
